import React, { useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../apis/api';
import { Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const DownloadSessionPDFButton = () => {
  const [loading, setLoading] = useState(false);

  const downloadPDF = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/pdf/sessions-data`);
      const sessions = response.data;

      if (!sessions || sessions.length === 0) {
        alert('No sessions found to generate PDF');
        setLoading(false);
        return;
      }

      const grouped = groupSessions(sessions);
      await generatePDFWithSmartPageBreaks(grouped);

    } catch (error) {
      console.error('Failed to download PDF:', error);
      alert('Failed to download PDF: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const generatePDFWithSmartPageBreaks = async (grouped) => {
  await loadMalayalamFont();
  
  // A4 landscape: 297mm x 210mm
  const pdf = new jsPDF('landscape', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth(); // 297mm
  const pageHeight = pdf.internal.pageSize.getHeight(); // 210mm
  const margin = 15; // Increased margin for better A4 layout
  const contentWidth = pageWidth - (margin * 2); // 267mm
  const contentHeight = pageHeight - (margin * 2); // 180mm
  
  console.log(`A4 Landscape PDF: ${pageWidth}mm x ${pageHeight}mm, Content: ${contentWidth}mm x ${contentHeight}mm`);
  
  let isFirstPage = true;
  const sortedDates = Object.keys(grouped).sort(); // Sort dates to ensure proper order

  for (const date of sortedDates) {
    console.log(`Processing date: ${date} with data:`, grouped[date]);
    
    // Create each date section separately to avoid content crossing
    const dateSection = await createDateSection(date, grouped[date]);
    
    // Start each date on a new page (except the first one) for data integrity
    if (!isFirstPage) {
      pdf.addPage();
      console.log(`Added new page for date: ${date}`);
    }
    
    await addSectionToPDF(pdf, dateSection, margin, contentWidth, contentHeight);
    isFirstPage = false;
  }

  // Add end marker
  pdf.setFontSize(14);
  pdf.setTextColor(120, 120, 120);
  pdf.text('— End of content —', pageWidth / 2, pageHeight - 10, { align: 'center' });
  pdf.save('sessions.pdf');
};

  const createDateSection = async (date, dateData) => {
    const container = document.createElement('div');
    container.style.cssText = `
      position: absolute;
      left: -10000px;
      top: -10000px;
      width: 1100px;
      background-color: white;
      font-family: 'Noto Sans Malayalam', 'Noto Sans', Arial, sans-serif;
      padding: 15px;
      font-size: 16px;
      line-height: 1.3;
      color: #000;
      box-sizing: border-box;
    `;

    const formattedDate = new Date(date).toDateString();
    
    // Get all venues for this date
    const allVenues = new Set();
    for (const timeSlot in dateData) {
      Object.keys(dateData[timeSlot]).forEach(v => allVenues.add(v));
    }
    const venues = Array.from(allVenues);

    let html = `
      <div style="text-align: center; font-size: 22px; font-weight: 700; margin-bottom: 15px; color: #2d3748; border-bottom: 2px solid #4299e1; padding-bottom: 8px;">
        Programme Schedule
      </div>
      <div style="font-size: 16px; font-weight: 600; margin-bottom: 12px; text-align: center; color: #1a365d; background: #f7fafc; padding: 8px; border-radius: 4px;">
        ${formattedDate}
      </div>
    `;

    // Create time slot sections that won't be split
    const timeSlots = Object.keys(dateData).sort();
    
    for (let timeIndex = 0; timeIndex < timeSlots.length; timeIndex++) {
      const slot = timeSlots[timeIndex];
      
      // Create a separate table for each time slot to prevent splitting
      html += `
        <div class="time-slot-section" style="margin-bottom: 15px; page-break-inside: avoid;">
          <table style="width: 100%; border-collapse: collapse; font-size: 11px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
            <thead>
              <tr style="background-color: #4a5568; color: white;">
                ${venues.map(venue => `
                  <th style="border: 1px solid #2d3748; padding: 10px 6px; text-align: center; font-weight: 600; font-size: 12px; width: ${100/venues.length}%;">
                    ${venue.toUpperCase()}
                  </th>
                `).join('')}
              </tr>
            </thead>
            <tbody>
      `;

      // Find max sessions in this time slot
      let maxSessions = 0;
      venues.forEach(venue => {
        const sessionsCount = dateData[slot][venue]?.length || 0;
        maxSessions = Math.max(maxSessions, sessionsCount);
      });

      maxSessions = Math.max(maxSessions, 1);

      // Create rows for this time slot
      for (let level = 0; level < maxSessions; level++) {
        html += `<tr style="min-height: 45px;">`;
        
        venues.forEach((venue, venueIndex) => {
          const sessions = dateData[slot][venue] || [];
          const session = sessions[level];
          const bgColor = venueIndex % 2 === 0 ? '#ffffff' : '#f8f9fa';
          
          html += `<td style="border: 1px solid #e2e8f0; padding: 8px 5px; background: ${bgColor}; vertical-align: top;">`;
          
          if (level === 0) {
            html += `<div style="font-weight: 700; color: #3182ce; font-size: 10px; margin-bottom: 5px; padding: 2px 5px; background: #ebf8ff; border-radius: 2px; display: inline-block;">${slot}</div>`;
          }
          
          if (session) {
            html += `
              <div style="font-weight: 600; font-size: 12px; color: #1a202c; margin-bottom: 3px; line-height: 1.2;">
                ${session.name || 'Untitled Session'}
              </div>
            `;
            
            if (session.session_speackers?.length) {
              html += `
                <div style="font-size: 9px; color: #4a5568; margin-bottom: 2px; font-style: italic; line-height: 1.1;">
                  <strong>Speakers:</strong> ${session.session_speackers.map(s => s.speaker?.heading || 'Unknown').join(', ')}
                </div>
              `;
            }
            
            if (session.sessionModerators?.length) {
              html += `
                <div style="font-size: 9px; color: #718096; line-height: 1.1;">
                  <strong>Moderator:</strong> ${session.sessionModerators.map(m => m.speaker?.heading || 'Unknown').join(', ')}
                </div>
              `;
            }
          } else if (level === 0 && sessions.length === 0) {
            html += `<div style="color: #a0aec0; font-style: italic; font-size: 10px;">No sessions</div>`;
          }
          
          html += `</td>`;
        });
        
        html += `</tr>`;
      }

      html += `</tbody></table></div>`;
    }

    container.innerHTML = html;
    document.body.appendChild(container);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return container;
  };

  const addSectionToPDF = async (pdf, container, margin, contentWidth, contentHeight) => {
    try {
      // Get all time slot sections
      const timeSlotSections = container.querySelectorAll('.time-slot-section');
      let currentY = margin;

      // Add title and date header first
      const titleElements = container.querySelector('div:first-child');
      const dateElements = container.querySelector('div:nth-child(2)');
      
      if (titleElements && dateElements) {
        const headerContainer = document.createElement('div');
        headerContainer.style.cssText = container.style.cssText;
        headerContainer.appendChild(titleElements.cloneNode(true));
        headerContainer.appendChild(dateElements.cloneNode(true));
        document.body.appendChild(headerContainer);

        // Wait for proper rendering
        await new Promise(resolve => setTimeout(resolve, 500));

        const headerCanvas = await html2canvas(headerContainer, {
          scale: 1.5,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          width: headerContainer.scrollWidth,
          height: headerContainer.scrollHeight,
          scrollX: 0,
          scrollY: 0
        });

        document.body.removeChild(headerContainer);

        const headerHeight = (headerCanvas.height * contentWidth) / headerCanvas.width;
        const headerImgData = headerCanvas.toDataURL('image/png', 0.95);
        
        pdf.addImage(headerImgData, 'PNG', margin, currentY, contentWidth, headerHeight);
        currentY += headerHeight + 10;
      }

      // Process each time slot section separately for data integrity
      for (let i = 0; i < timeSlotSections.length; i++) {
        const section = timeSlotSections[i];
        
        // Create a temporary container for this section only
        const sectionContainer = document.createElement('div');
        sectionContainer.style.cssText = container.style.cssText;
        sectionContainer.appendChild(section.cloneNode(true));
        document.body.appendChild(sectionContainer);

        // Wait for proper rendering
        await new Promise(resolve => setTimeout(resolve, 600));

        const sectionCanvas = await html2canvas(sectionContainer, {
          scale: 1.5,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          width: sectionContainer.scrollWidth,
          height: sectionContainer.scrollHeight,
          scrollX: 0,
          scrollY: 0
        });

        document.body.removeChild(sectionContainer);

        const sectionWidth = contentWidth;
        const sectionHeight = (sectionCanvas.height * sectionWidth) / sectionCanvas.width;

        // Check if section fits on current page
        if (currentY + sectionHeight > pdf.internal.pageSize.getHeight() - margin) {
          pdf.addPage();
          currentY = margin;
        }

        const sectionImgData = sectionCanvas.toDataURL('image/png', 0.95);
        pdf.addImage(sectionImgData, 'PNG', margin, currentY, sectionWidth, sectionHeight);
        currentY += sectionHeight + 3; // Small gap between sections
      }

    } catch (error) {
      console.error('Error adding section to PDF:', error);
    } finally {
      if (container.parentNode) {
        document.body.removeChild(container);
      }
    }
  };

  const loadMalayalamFont = async () => {
    return new Promise((resolve) => {
      const existingLink = document.querySelector('link[href*="Noto+Sans+Malayalam"]');
      if (existingLink) {
        setTimeout(resolve, 200); // Reduced wait time for existing fonts
        return;
      }

      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+Malayalam:wght@400;500;600;700&display=swap';
      link.rel = 'stylesheet';
      
      // Use faster loading strategy
      link.onload = () => {
        // Check if font is actually loaded
        const testElement = document.createElement('div');
        testElement.style.fontFamily = 'Noto Sans Malayalam';
        testElement.style.position = 'absolute';
        testElement.style.visibility = 'hidden';
        testElement.textContent = 'test';
        document.body.appendChild(testElement);
        
        setTimeout(() => {
          document.body.removeChild(testElement);
          resolve();
        }, 300);
      };
      
      link.onerror = () => {
        console.warn('Malayalam font failed to load, using fallback');
        setTimeout(resolve, 50);
      };
      
      document.head.appendChild(link);
    });
  };

  const groupSessions = (sessions) => {
    const grouped = {};
    sessions.forEach((s) => {
      const date = new Date(s.date).toISOString().split("T")[0];
      const timeFrom = s.time_from?.slice(0, 5);
      const timeTo = s.time_to?.slice(0, 5);
      const timeSlot = `${timeFrom} - ${timeTo}`;
      const venue = s.venue?.name || "Unknown Venue";
      if (!grouped[date]) grouped[date] = {};
      if (!grouped[date][timeSlot]) grouped[date][timeSlot] = {};
      if (!grouped[date][timeSlot][venue]) grouped[date][timeSlot][venue] = [];
      grouped[date][timeSlot][venue].push(s);
    });
    return grouped;
  };

  return (
    <button onClick={downloadPDF} title="Download PDF" disabled={loading}>
      {loading ? (
        <span className="text-sm text-gray-600">Generating...</span>
      ) : (
        <Download className="w-7 h-7 text-blue-500 hover:text-blue-600 transition-transform transform hover:scale-110 duration-200" />
      )}
    </button>
  );
};

export default DownloadSessionPDFButton;
// import React, { useState } from 'react';
// import axios from 'axios';
// import { Download } from 'lucide-react';
// import { BASE_URL } from '../../apis/api';
// import jsPDF from 'jspdf';
// import 'jspdf-autotable';

// import notoMalayalam from '../../fonts/NotoSansMalayalam-Regular.base64';

// const DownloadSessionPDFButton = ({ label }) => {
//   const [loading, setLoading] = useState(false);

//   const downloadPDF = async () => {
//     try {
//       setLoading(true);
//       const response = await axios.get(`${BASE_URL}/pdf/sessions-data`);
//       const sessions = response.data;

//       if (!sessions || sessions.length === 0) {
//         alert('No sessions found to generate PDF');
//         return;
//       }

//       const grouped = groupSessions(sessions);
//       generatePDF(grouped);
//     } catch (err) {
//       console.error(err);
//       alert('Failed to generate PDF');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const groupSessions = (sessions) => {
//     const grouped = {};
//     sessions.forEach((s) => {
//       const date = new Date(s.date).toISOString().split("T")[0];
//       const timeFrom = s.time_from?.slice(0, 5);
//       const timeTo = s.time_to?.slice(0, 5);
//       const timeSlot = `${timeFrom} - ${timeTo}`;
//       const venue = s.venue?.name || "Unknown Venue";
//       if (!grouped[date]) grouped[date] = {};
//       if (!grouped[date][timeSlot]) grouped[date][timeSlot] = {};
//       if (!grouped[date][timeSlot][venue]) grouped[date][timeSlot][venue] = [];
//       grouped[date][timeSlot][venue].push(s);
//     });
//     return grouped;
//   };

//   const generatePDF = (grouped) => {
//   const doc = new jsPDF('landscape', 'mm', 'a4');
//   const pageWidth = doc.internal.pageSize.getWidth();
//   const pageHeight = doc.internal.pageSize.getHeight();
//   const margin = 15;
//   const maxRowsPerPage = 4;

//   // Register Malayalam font
//   doc.addFileToVFS('NotoSansMalayalam.ttf', notoMalayalam);
//   doc.addFont('NotoSansMalayalam.ttf', 'NotoMalayalam', 'normal');
//   doc.setFont('NotoMalayalam');
//   doc.setFontSize(18);
//   doc.text('Programme Schedule', pageWidth / 2, margin, { align: 'center' });

//   let currentY = margin + 10;

//   const sortedDates = Object.keys(grouped).sort();

//   sortedDates.forEach((date) => {
//     const formattedDate = new Date(date).toDateString();
//     const dateData = grouped[date];
//     const timeSlots = Object.keys(dateData).sort();

//     // Collect all unique venues for this date
//     const venues = Array.from(new Set(timeSlots.flatMap(slot => Object.keys(dateData[slot]))));
//     const equalColWidth = (pageWidth - 2 * margin) / venues.length;

//     let dateRendered = false;

//     timeSlots.forEach((timeSlot) => {
//       const rows = [];

//       let maxSessions = 0;
//       venues.forEach(venue => {
//         maxSessions = Math.max(maxSessions, dateData[timeSlot][venue]?.length || 0);
//       });

//       for (let i = 0; i < maxSessions; i++) {
//         const row = venues.map((venue) => {
//           const session = dateData[timeSlot][venue]?.[i];
//           if (!session) return '';
//           let text = `⏰ ${timeSlot}\n`;

//           text += `${session.name || 'Untitled Session'}`;

//           if (session.session_speackers?.length) {
//             const speakers = session.session_speackers.map(s => s.speaker?.heading || '').join(', ');
//             text += `\nSpeakers: ${speakers}`;
//           }
//           if (session.session_moderators?.length) {
//             const moderators = session.session_moderators.map(m => m.speaker?.heading || '').join(', ');
//             text += `\nModerator: ${moderators}`;
//           }

//           return text;
//         });

//         rows.push(row);

//         const isLastRow = i === maxSessions - 1;

//         if (rows.length === maxRowsPerPage || isLastRow) {
//           // Only render date heading when we are about to render the first table block for that date
//           if (!dateRendered) {
//             if (currentY > pageHeight - 40) {
//               doc.addPage();
//               currentY = margin;
//             }
//             doc.setFontSize(14);
//             doc.setFont('NotoMalayalam');
//             doc.text(formattedDate, pageWidth / 2, currentY, { align: 'center' });
//             currentY += 6;
//             dateRendered = true;
//           }

//           doc.autoTable({
//             head: [venues.map(v => v.toUpperCase())],
//             body: rows,
//             startY: currentY,
//             styles: {
//               font: 'NotoMalayalam',
//               fontSize: 9,
//               cellPadding: 2,
//               valign: 'top',
//               overflow: 'linebreak',
//             },
//             headStyles: {
//               fillColor: [74, 85, 104],
//               textColor: 255,
//             },
//             columnStyles: venues.reduce((acc, _, i) => {
//               acc[i] = { cellWidth: equalColWidth };
//               return acc;
//             }, {}),
//             margin: { left: margin, right: margin },
//             didParseCell: (data) => {
//               const lines = data.cell.raw.split('\n');
//               if (lines.length > 1 && data.row.index >= 0) {
//                 data.cell.styles.fontStyle = 'bold';
//               }
//             },
//             didDrawPage: (data) => {
//               currentY = data.cursor.y + 10;

//               if (currentY > pageHeight - 40) {
//                 doc.addPage();
//                 currentY = margin;
//               }
//             }
//           });

//           rows.length = 0;
//         }
//       }
//     });
//   });

//   doc.setFontSize(12);
//   doc.setTextColor(120, 120, 120);
//   doc.text('— End of content —', pageWidth / 2, pageHeight - 10, { align: 'center' });

//   doc.save('sessions.pdf');
// };


//   return (
//     <button
//       onClick={downloadPDF}
//       disabled={loading}
//       title="Download PDF"
//       className={`transition duration-150 ${label ? 'px-4 py-2 rounded-md border border-blue-500 text-blue-600 hover:bg-blue-50 text-sm font-medium' : ''}`}
//     >
//       {loading ? (
//         'Generating...'
//       ) : label ? (
//         label
//       ) : (
//         <Download className="w-7 h-7 text-blue-500 hover:text-blue-600 transition-transform transform hover:scale-110 duration-200" />
//       )}
//     </button>
//   );
// };

// export default DownloadSessionPDFButton;
